// =====================================================
// ODONTOGRAM INLINE DISPLAY - Visualización compacta
// Muestra la selección del odontograma dentro de la línea
// =====================================================
import { Edit3 } from 'lucide-react';
import type { OdontogramSelection } from './OdontogramModal';

interface OdontogramInlineDisplayProps {
  selection: OdontogramSelection | null;
  isChild: boolean;
  onEdit?: () => void;
  compact?: boolean;
}

// Mini odontograma visual
function MiniOdontogram({
  selectedTeeth,
  isChild,
}: {
  selectedTeeth: Set<string>;
  isChild: boolean;
}) {
  const upperLeft = isChild ? ['55', '54', '53', '52', '51'] : ['18', '17', '16', '15', '14', '13', '12', '11'];
  const upperRight = isChild ? ['61', '62', '63', '64', '65'] : ['21', '22', '23', '24', '25', '26', '27', '28'];
  const lowerLeft = isChild ? ['85', '84', '83', '82', '81'] : ['48', '47', '46', '45', '44', '43', '42', '41'];
  const lowerRight = isChild ? ['71', '72', '73', '74', '75'] : ['31', '32', '33', '34', '35', '36', '37', '38'];

  return (
    <div className="inline-flex flex-col gap-0.5">
      {/* Arcada superior */}
      <div className="flex gap-px">
        <div className="flex gap-px pr-1 border-r border-gray-200">
          {upperLeft.map((n) => (
            <div
              key={n}
              className={`w-2 h-2.5 rounded-sm ${
                selectedTeeth.has(n) ? 'bg-blue-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <div className="flex gap-px pl-1">
          {upperRight.map((n) => (
            <div
              key={n}
              className={`w-2 h-2.5 rounded-sm ${
                selectedTeeth.has(n) ? 'bg-blue-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
      {/* Arcada inferior */}
      <div className="flex gap-px">
        <div className="flex gap-px pr-1 border-r border-gray-200">
          {lowerLeft.map((n) => (
            <div
              key={n}
              className={`w-2 h-2.5 rounded-sm ${
                selectedTeeth.has(n) ? 'bg-blue-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <div className="flex gap-px pl-1">
          {lowerRight.map((n) => (
            <div
              key={n}
              className={`w-2 h-2.5 rounded-sm ${
                selectedTeeth.has(n) ? 'bg-blue-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function OdontogramInlineDisplay({
  selection,
  isChild,
  onEdit,
  compact = false,
}: OdontogramInlineDisplayProps) {
  if (!selection) {
    return (
      <button
        onClick={onEdit}
        className="flex items-center gap-1.5 px-2 py-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
      >
        <span className="text-sm">🦷</span>
        <span>Sin selección</span>
        {onEdit && <Edit3 className="h-3 w-3" />}
      </button>
    );
  }

  // Calcular todos los dientes seleccionados
  const allTeeth = new Set<string>();
  
  // Dientes individuales
  selection.teeth.forEach((t) => allTeeth.add(t));
  
  // Dientes de cuadrantes
  const QUADRANT_TEETH_ADULT: Record<string, string[]> = {
    Q1: ['18', '17', '16', '15', '14', '13', '12', '11'],
    Q2: ['21', '22', '23', '24', '25', '26', '27', '28'],
    Q3: ['31', '32', '33', '34', '35', '36', '37', '38'],
    Q4: ['41', '42', '43', '44', '45', '46', '47', '48'],
  };
  
  const QUADRANT_TEETH_CHILD: Record<string, string[]> = {
    Q1: ['55', '54', '53', '52', '51'],
    Q2: ['61', '62', '63', '64', '65'],
    Q3: ['71', '72', '73', '74', '75'],
    Q4: ['81', '82', '83', '84', '85'],
  };

  const QUADRANT_TEETH = isChild ? QUADRANT_TEETH_CHILD : QUADRANT_TEETH_ADULT;

  selection.quadrants.forEach((q) => {
    QUADRANT_TEETH[q]?.forEach((t) => allTeeth.add(t));
  });

  // Dientes de arcadas
  const ARCADE_QUADRANTS: Record<string, string[]> = {
    SUPERIOR: ['Q1', 'Q2'],
    INFERIOR: ['Q3', 'Q4'],
  };

  selection.arcades.forEach((a) => {
    ARCADE_QUADRANTS[a]?.forEach((q) => {
      QUADRANT_TEETH[q]?.forEach((t) => allTeeth.add(t));
    });
  });

  if (compact) {
    return (
      <div
        onClick={onEdit}
        className={`
          inline-flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg
          ${onEdit ? 'cursor-pointer hover:bg-blue-100' : ''}
          transition-colors
        `}
      >
        <MiniOdontogram selectedTeeth={allTeeth} isChild={isChild} />
        <span className="text-xs font-medium text-blue-700">
          {allTeeth.size} {allTeeth.size === 1 ? 'pieza' : 'piezas'}
        </span>
        {onEdit && <Edit3 className="h-3 w-3 text-blue-500" />}
      </div>
    );
  }

  return (
    <div
      onClick={onEdit}
      className={`
        flex items-center gap-3 px-3 py-2 bg-gradient-to-r from-blue-50 to-cyan-50 
        border border-blue-200 rounded-xl
        ${onEdit ? 'cursor-pointer hover:from-blue-100 hover:to-cyan-100' : ''}
        transition-all
      `}
    >
      {/* Mini visualización */}
      <MiniOdontogram selectedTeeth={allTeeth} isChild={isChild} />

      {/* Información de selección */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {/* Badges para arcadas */}
          {selection.arcades.map((a) => (
            <span
              key={a}
              className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded-full uppercase"
            >
              {a === 'SUPERIOR' ? '↑ Superior' : '↓ Inferior'}
            </span>
          ))}

          {/* Badges para cuadrantes */}
          {selection.quadrants.map((q) => (
            <span
              key={q}
              className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full"
            >
              {q}
            </span>
          ))}
        </div>

        {/* Dientes individuales */}
        {selection.teeth.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {selection.teeth.slice(0, 8).map((t) => (
              <span
                key={t}
                className="inline-flex items-center justify-center w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded"
              >
                {t}
              </span>
            ))}
            {selection.teeth.length > 8 && (
              <span className="inline-flex items-center px-1.5 h-5 bg-blue-200 text-blue-700 text-[10px] font-bold rounded">
                +{selection.teeth.length - 8}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Contador y botón editar */}
      <div className="flex items-center gap-2">
        <div className="text-right">
          <div className="text-lg font-bold text-blue-600">{allTeeth.size}</div>
          <div className="text-[10px] text-gray-500 uppercase">
            {allTeeth.size === 1 ? 'pieza' : 'piezas'}
          </div>
        </div>
        {onEdit && (
          <div className="p-1.5 bg-white rounded-lg shadow-sm">
            <Edit3 className="h-4 w-4 text-blue-500" />
          </div>
        )}
      </div>
    </div>
  );
}

// Exportar índice del componente
export { OdontogramModal } from './OdontogramModal';
export type { OdontogramSelection, SelectionMode } from './OdontogramModal';
